import React, { useEffect, useState } from 'react';
import { UpdateCompliance } from '@services/Compliance/Index.jsx';
import SweetAlert from '@components/Feedback/SweetAlert/Index.jsx';
import ButtonComp from '@components/UI/Button/Index.jsx';
import ValidationsCompliance from '@validations/Compliance/Validations.jsx';
import InputTextModalComp from '@components/UI/InputTextModal/Index.jsx';
import SelectOptionModalComp from '@components/UI/SelectOptionModal/Index.jsx';
import { tipoComplianceOptions } from '@enums/Compliance/Index.jsx';
import { useFetchDataCascade } from '@hooks/api/useFetchDataCascade.js';
import { Modal } from 'antd';

function EditModal({ onUpdate, visible, closeModal, selectedRow }) {
  const [codigo, setCodigo] = useState('');

  const {
    yearOptions,
    serviceOptions,
    typeOptions,
    processOptions,
    yearLoading,
    serviceLoading,
    typeLoading,
    processLoading,
    fetchYears,
    fetchServicesByYear,
    fetchTypesByServiceId,
    fetchProcessosByType,
    setServiceOptions,
    setTypeOptions,
    setProcessOptions,
  } = useFetchDataCascade();

  const [tempValues, setTempValues] = useState({
    tipoCompliance: null,
    ano: null,
    tipoServico: null,
    tipoServicoAssociado: null,
    nomeProcesso: null,
    descricao: "",
  });

  useEffect(() => {
    fetchYears();
  }, []);

  useEffect(() => {
    if (selectedRow) {
      setCodigo(selectedRow.codigo);
      setTempValues({
        ano: { value: selectedRow.ano, label: selectedRow.ano },
        tipoCompliance: tipoComplianceOptions.find(option => option.value === selectedRow.tipoCompliance),
        tipoServico: { value: selectedRow.idTipoServico, label: selectedRow.tipoServico },
        tipoServicoAssociado: { value: selectedRow.idTipoServicoAssociado, label: selectedRow.tipoServicoAssociado },
        nomeProcesso: { value: selectedRow.idProcesso, label: selectedRow.nomeProcesso },
        descricao: selectedRow.descricao,
      });

      if (selectedRow.ano) {
        fetchServicesByYear(selectedRow.ano);
      }
      if (selectedRow.idTipoServico) {
        fetchTypesByServiceId(selectedRow.idTipoServico);
      }
      if (selectedRow.tipoServicoAssociado) {
        fetchProcessosByType(selectedRow.idTipoServicoAssociado);
      }
    }
  }, [selectedRow]);

  const checkIsDirty = () => {
    return (
      tempValues.descricao !== selectedRow?.descricao
    );
  };

  const handleTempChange = (field, value) => {
    setTempValues((prev) => ({
      ...prev,
      [field]: value,
    }));

    if (field === "tipoCompliance") {
      const isServico = value?.value === 2;

      if (isServico) {
        return {
          ...prev,
          [field]: value, // Atualiza o tipo de cadastro
        };
      } else if (value?.value === 1) {
        return {
          ...prev,
          [field]: value,
        };
      }
    }

    if (field === "ano") {
      setServiceOptions([]);
      setTypeOptions([]);
      setProcessOptions([]);
      setTempValues((prev) => ({
        ...prev,
        tipoServico: null,
        tipoServicoAssociado: null,
        nomeProcesso: null,
      }));
      if (value) {
        fetchServicesByYear(value.value);
      }
    }

    if (field === "tipoServico") {
      setTypeOptions([]);
      setProcessOptions([]);
      setTempValues((prev) => ({
        ...prev,
        tipoServicoAssociado: null,
        nomeProcesso: null,
      }));
      if (value) {
        fetchTypesByServiceId(value.value);
      }
    }

    if (field === "tipoServicoAssociado") {
      setProcessOptions([]);
      setTempValues((prev) => ({
        ...prev,
        nomeProcesso: null,
      }));
      if (value) {
        fetchProcessosByType(value.value);
      }
    }
  };

  const onSubmit = async () => {
    try {
      if (!checkIsDirty()) {
        SweetAlert.error("Erro", "Nenhuma alteração foi feita nos dados.");
        return;
      }

      const { validateFields } = ValidationsCompliance();
      const errors = validateFields({
        ...tempValues,
      });

      if (Object.keys(errors).length > 0) {
        const errorMessage = Object.values(errors).join("\n");
        SweetAlert.error("Erro na Validação", errorMessage);
        return;
      }

      const payload = {
        id: selectedRow.id,
        descricao: tempValues.descricao,
      };

      SweetAlert.loading();

      const result = await UpdateCompliance(selectedRow.id, payload);

      onUpdate({ ...result });

      closeModal();

      SweetAlert.success('Atualização Concluída!', 'Os dados foram atualizados com sucesso.');
    } catch (error) {
      const errorMessage = error.parsed?.message || 'Não foi possível atualizar o compliance.';

      SweetAlert.error('Erro ao atualizar', errorMessage);
      console.error("Erro ao salvar atualizações", errorMessage);
    }
  };

  const handleCloseWithReset = () => {
    setTempValues({
      ano: { value: selectedRow.ano, label: selectedRow.ano },
      tipoCompliance: tipoComplianceOptions.find(option => option.value === selectedRow.tipoCompliance),
      tipoServico: { value: selectedRow.idTipoServico, label: selectedRow.tipoServico },
      tipoServicoAssociado: { value: selectedRow.idTipoServicoAssociado, label: selectedRow.tipoServicoAssociado },
      nomeProcesso: { value: selectedRow.idProcesso, label: selectedRow.nomeProcesso },
      descricao: selectedRow.descricao,
    });
    closeModal();
  };

  return (
    <Modal
      getContainer={false}
      open={visible}
      onCancel={handleCloseWithReset}
      centered
      footer={null}
      width={800}
      title={<h5 className="fw-bold">Edição de Compliance</h5>}
    >
      <hr />

      <InputTextModalComp
        label="Código"
        value={codigo}
        placeholder="Código"
        hoverMessage={"Campo desabilitado para edição"}
        disabled
      />

      <SelectOptionModalComp
        label={"Ano"}
        options={yearOptions}
        value={tempValues.ano}
        onChange={(selected) => handleTempChange('ano', selected)}
        hoverMessage="Campo desabilitado para edição"
        isLoading={yearLoading}
        isDisabled
      />

      <SelectOptionModalComp
        label={"Compliance"}
        options={tipoComplianceOptions}
        value={tempValues.tipoCompliance}
        onChange={(selected) => handleTempChange('tipoCompliance', selected)}
        hoverMessage="Campo desabilitado para edição"
        isLoading={yearLoading}
        isDisabled
      />

      <SelectOptionModalComp
        label="Serviço"
        options={serviceOptions}
        value={tempValues.tipoServico}
        onChange={(selected) => handleTempChange('tipoServico', selected)}
        hoverMessage="Campo desabilitado para edição"
        isLoading={serviceLoading}
        isDisabled
      />

      <SelectOptionModalComp
        label="Tipo"
        options={typeOptions}
        value={tempValues.tipoServicoAssociado}
        onChange={(selected) => handleTempChange('tipoServicoAssociado', selected)}
        hoverMessage="Campo desabilitado para edição"
        isLoading={typeLoading}
        isDisabled
      />

      <SelectOptionModalComp
        label="Processo"
        options={processOptions}
        value={tempValues.nomeProcesso}
        onChange={(selected) => handleTempChange('nomeProcesso', selected)}
        hoverMessage="Campo desabilitado para edição"
        isLoading={processLoading}
        isDisabled
      />

      <InputTextModalComp
        label="Descrição"
        value={tempValues.descricao}
        onChange={(e) => handleTempChange("descricao", e.target.value)}
        placeholder="Preencher com uma descrição"
        hoverMessage={"Preencher com uma descrição"}
        maxLength={255}
      />

      <div className="d-flex justify-content-end gap-2 mt-4">
        <ButtonComp
          title="ATUALIZAR"
          buttonColor="#2b97be"
          classnameIcon="fa-regular fa-floppy-disk"
          onClick={onSubmit}
          hoverMessage={"Botão para confirmar a atualização"}
        />
        <ButtonComp
          title="CANCELAR"
          buttonColor="#dc3545"
          classnameIcon="fa-regular fa-circle-xmark"
          onClick={handleCloseWithReset}
          hoverMessage={"Botão para cancelar a atualização"}
        />
      </div>
    </Modal>
  );
}

export default EditModal;